// ============================================================================
// STAKEHOLDER WORKFLOW STATUS CONFIG
// Default workflow statuses for stakeholder item_types (customer, supplier,
// vendor, partner, contractor, contact). Mirrors CONTACT_ACCOUNT_STATUS_VALUES
// in src/lib/contacts/accountStanding.ts so a status change applied through
// StatusTransition / DetailsSidebar can be persisted as the contact's
// account_status field.
// Date: April 2026
// ============================================================================

import type { OperationalWorkflowStatusDefinition } from './operationalStatusConfig';
import { SUPPLIER_STATUS_VALUES } from '@/lib/contacts/accountStanding';

const STAKEHOLDER_ITEM_TYPES = new Set([
	'contact',
	'customer',
	'supplier',
	'partner',
	'contractor',
	'company',
]);

const STAKEHOLDER_WORKFLOW_STATUS_DEFINITIONS: OperationalWorkflowStatusDefinition[] = [
	{
		key: 'draft',
		label: 'Draft',
		color: 'gray',
		description: 'Stakeholder being onboarded — not yet trading',
		isStart: true,
		isSystem: true,
	},
	{
		key: 'active',
		label: 'Active',
		color: 'green',
		description: 'Active account in good standing',
		isSystem: true,
	},
	{
		key: 'cod',
		label: 'COD / Prepaid',
		color: 'cyan',
		description: 'Cash on delivery or prepayment required',
		isSystem: true,
	},
	{
		key: 'on_hold',
		label: 'On Hold',
		color: 'amber',
		description: 'Held pending finance, legal or compliance review',
		isSystem: true,
	},
	{
		key: 'suspended_over_limit',
		label: 'Over Credit Limit',
		color: 'orange',
		description: 'Suspended because balance is over the approved limit',
		isSystem: true,
	},
	{
		key: 'suspended_non_payment',
		label: 'Overdue / Non-Payment',
		color: 'red',
		description: 'Suspended because outstanding amounts are unpaid',
		isSystem: true,
	},
	{
		key: 'inactive',
		label: 'Inactive',
		color: 'slate',
		description: 'Not currently trading — archived',
		isEnd: true,
		isSystem: true,
	},
	// Supplier-specific statuses (only shown for contact_type = 'supplier')
	{
		key: 'preferred_supplier',
		label: 'Preferred Supplier',
		color: 'blue',
		description: 'Primary supplier — first choice for procurement',
		isSystem: true,
	},
	{
		key: 'approved_supplier',
		label: 'Approved Supplier',
		color: 'green',
		description: 'Compliant supplier cleared for normal purchasing',
		isSystem: true,
	},
	{
		key: 'non_compliant_supplier',
		label: 'Non-Compliant',
		color: 'amber',
		description: 'Compliance issues — expenses allowed but require confirmation',
		isSystem: true,
	},
	{
		key: 'restricted_supplier',
		label: 'Restricted',
		color: 'red',
		description: 'Blocked from new purchases pending review',
		isSystem: true,
	},
];

function normalizeKey(value?: string | null): string {
	return typeof value === 'string'
		? value.trim().toLowerCase().replace(/[\s-]+/g, '_')
		: '';
}

export function isStakeholderWorkflowItemType(itemType?: string | null): boolean {
	return STAKEHOLDER_ITEM_TYPES.has(normalizeKey(itemType));
}

export function getDefaultStakeholderWorkflowStatuses(): OperationalWorkflowStatusDefinition[] {
	return STAKEHOLDER_WORKFLOW_STATUS_DEFINITIONS.map((status) => ({ ...status }));
}

/**
 * Map a stakeholder workflow status key to a contact account_status value.
 * Returns null when the key isn't a known account status.
 */
export function mapStakeholderStatusToAccountStatus(status?: string | null): string | null {
	const normalized = normalizeKey(status);
	if (!normalized) return null;

	const knownAccountStatuses = new Set([
		'draft',
		'active',
		'cod',
		'on_hold',
		'suspended_over_limit',
		'suspended_non_payment',
		'inactive',
		...SUPPLIER_STATUS_VALUES,
	]);

	return knownAccountStatuses.has(normalized) ? normalized : null;
}
